import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { modifier } from 'ember-modifier';
import Modal from './modal';
import type { ModalSize } from './modal';
import Button from './button';
import type { ButtonIntent } from './button';
import AlertBanner from './alert-banner';
import { PhWarning, PhTrash, PhCheckCircle, PhInfo } from 'ember-phosphor-icons';

/**
 * Visual intent for the confirmation dialog. Maps onto the canonical
 * --warning / --danger / --success / --info token recipes plus the
 * matching Button intent for the confirm button.
 */
export type ConfirmationIntent = 'warning' | 'danger' | 'success' | 'info';

/**
 * Which Button @intent fires the confirm action for each ConfirmationIntent.
 * Reuses the canonical `ButtonIntent` union from button.gts (rather than
 * declaring a parallel union) so future Button intent additions / renames
 * propagate here automatically.
 */
const CONFIRM_BUTTON_INTENT: Record<ConfirmationIntent, ButtonIntent> = {
  warning: 'warning',
  danger: 'danger',
  success: 'success',
  info: 'primary',
};

export interface ConfirmationDialogSignature {
  Args: {
    /** Controls whether the dialog is visible */
    isOpen: boolean;
    /** The title displayed in the dialog header */
    title: string;
    /** Max-width of the dialog panel @default "md" */
    size?: ModalSize;
    /** Visual intent of the dialog
     * - 'warning': Amber styling with warning icon
     * - 'danger': Red styling with trash icon
     * - 'success': Emerald styling with check icon
     * - 'info': Blue styling with info icon
     * @default 'warning'
     */
    intent?: ConfirmationIntent;
    /** Text for the confirm button @default 'Confirm' */
    confirmText?: string;
    /** Text for the cancel button @default 'Cancel' */
    cancelText?: string;
    /** User must type this exact text to enable confirm button */
    typeToConfirm?: string;
    /** Hint text for type-to-confirm input */
    typeToConfirmHint?: string;
    /** Externally disable the confirm button (e.g. a required acknowledgment is unchecked) */
    confirmDisabled?: boolean;
    /** Shows loading state on confirm button */
    isRunning?: boolean;
    /** Error message to display (triggers shake animation) */
    error?: string | null;
    /** Called when the user confirms */
    onConfirm: () => void | Promise<void>;
    /** Called when the user cancels or closes */
    onCancel: () => void;
  };
  Blocks: {
    /** Main content of the dialog body */
    default: [];
    /** Optional warning message above main content */
    warning?: [];
  };
  Element: HTMLDivElement;
}

/**
 * Confirmation modal with optional type-to-confirm gating, error feedback, and loading state.
 * Intent colors flow through the wrapper’s --conf-* variables.
 */
export default class ConfirmationDialog extends Component<ConfirmationDialogSignature> {
  @tracked confirmationInput = '';
  @tracked private _shaking = false;

  /**
   * Modifier that watches @error — when it changes to a new truthy value,
   * triggers the shake animation for 500ms.
   */
  watchError = modifier((_element: HTMLElement, [error]: [string | null | undefined]) => {
    if (error) {
      this._shaking = true;
      const timer = setTimeout(() => {
        this._shaking = false;
      }, 500);

      return () => clearTimeout(timer);
    }
    return undefined;
  });

  get intent(): ConfirmationIntent {
    return this.args.intent ?? 'warning';
  }

  get size() {
    return this.args.size ?? 'md';
  }

  get confirmButtonIntent(): ButtonIntent {
    return CONFIRM_BUTTON_INTENT[this.intent];
  }

  get confirmText() {
    return this.args.confirmText ?? 'Confirm';
  }

  get cancelText() {
    return this.args.cancelText ?? 'Cancel';
  }

  get typeToConfirmHint() {
    if (this.args.typeToConfirmHint) {
      return this.args.typeToConfirmHint;
    }
    return `Type "${this.args.typeToConfirm}" to confirm`;
  }

  get isConfirmDisabled() {
    if (this.args.isRunning) return true;
    if (this.args.confirmDisabled) return true;
    if (this.args.typeToConfirm) {
      return this.confirmationInput.trim() !== this.args.typeToConfirm;
    }
    return false;
  }

  handleInputChange = (event: Event) => {
    this.confirmationInput = (event.target as HTMLInputElement).value;
  };

  handleConfirm = () => {
    this.confirmationInput = '';
    this.args.onConfirm();
  };

  handleCancel = () => {
    this.confirmationInput = '';
    this.args.onCancel();
  };

  <template>
    <Modal
      @isOpen={{@isOpen}}
      @onClose={{this.handleCancel}}
      @size={{this.size}}
      @closeOnEscapeKey={{true}}
      @allowCloseButton={{false}}
      data-test-confirmation-dialog
      ...attributes
    >
      {{! display:contents preserves modal layout while the wrapper supplies intent variables to descendants. }}
      <div class="app-confirmation app-confirmation--{{this.intent}}">
        <div data-test-confirmation-header class="app-confirmation-header">
          <div class="app-confirmation-icon" data-test-confirmation-icon>
            {{#if (eq this.intent "danger")}}
              <PhTrash @size={{22}} />
            {{else if (eq this.intent "success")}}
              <PhCheckCircle @size={{22}} />
            {{else if (eq this.intent "info")}}
              <PhInfo @size={{22}} />
            {{else}}
              <PhWarning @size={{22}} />
            {{/if}}
          </div>
          <h3 data-test-confirmation-title class="app-confirmation-title">
            {{@title}}
          </h3>
        </div>

        <div
          data-test-confirmation-body
          class="app-confirmation-body {{if this._shaking 'reimagined-shake'}}"
          {{this.watchError @error}}
        >
          {{#if (has-block "warning")}}
            <AlertBanner @intent="warning">
              {{yield to="warning"}}
            </AlertBanner>
          {{/if}}

          <div class="app-confirmation-message">
            {{yield}}
          </div>

          {{#if @typeToConfirm}}
            <label class="app-confirmation-input-label">
              <span class="app-confirmation-input-hint">{{this.typeToConfirmHint}}</span>
              <input
                type="text"
                value={{this.confirmationInput}}
                {{on "input" this.handleInputChange}}
                placeholder={{@typeToConfirm}}
                class="app-input app-input--md font-mono"
                data-test-confirmation-input
              />
            </label>
          {{/if}}

          {{#if @error}}
            <AlertBanner @intent="warning" data-test-confirmation-error>
              {{@error}}
            </AlertBanner>
          {{/if}}
        </div>

        <div data-test-confirmation-footer class="app-confirmation-footer">
          <Button
            @appearance="outlined"
            @intent="default"
            @isDisabled={{@isRunning}}
            data-test-confirmation-cancel
            {{on "click" this.handleCancel}}
          >
            {{this.cancelText}}
          </Button>
          <Button
            @intent={{this.confirmButtonIntent}}
            @isRunning={{@isRunning}}
            @isDisabled={{this.isConfirmDisabled}}
            data-test-confirmation-confirm
            {{on "click" this.handleConfirm}}
          >
            {{this.confirmText}}
          </Button>
        </div>
      </div>
    </Modal>
  </template>
}
